<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useTranslation } from "i18next-vue";
import type {
  ReportImage,
  MergedReportImage,
} from "@/types/report-images.types";
import type { Section } from "@/types/sections.types";
import {
  useUpdateReportImage,
  useCreateReportImage,
} from "@/stores/queries/report-images.query";
import { useGetReportSectionsTree } from "@/stores/queries";
import VisualiseImage from "../findings/VisualiseImage.vue";

// Accept both ReportImage and MergedReportImage
type ImageProp = ReportImage | MergedReportImage | null | undefined;

const props = defineProps<{
  modelValue: boolean;
  image: ImageProp;
  reportId: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "saved"): void;
}>();

const { t } = useTranslation();

const reportIdRef = computed(() => props.reportId);
const { mutateAsync: updateImage, isPending: isUpdating } =
  useUpdateReportImage(reportIdRef);
const { mutateAsync: createReportImage, isPending: isCreating } =
  useCreateReportImage(reportIdRef);
const { data: sectionsTree } = useGetReportSectionsTree(reportIdRef);

const isSaving = computed(() => isUpdating.value || isCreating.value);

// (A) Identifiant photo : provient du DiagnosticImage — soit via merged.photoNumber,
// soit via la relation diagnosticImage d'un ReportImage.
const currentPhotoNumber = computed<number | null>(() => {
  const img = props.image;
  if (!img) return null;
  if ("photoNumber" in img && img.photoNumber != null) return img.photoNumber;
  if ("diagnosticImage" in img && img.diagnosticImage?.photoNumber != null)
    return img.diagnosticImage.photoNumber;
  return null;
});

// Build section title path map from tree
const sectionTitlePaths = computed(() => {
  const map = new Map<number, string>();
  if (!sectionsTree.value) return map;
  const build = (sections: Section[], parentPath?: string) => {
    for (const section of sections) {
      const path = parentPath
        ? `${parentPath} > ${section.title}`
        : section.title;
      map.set(section.id, path);
      if (section.children?.length) {
        build(section.children, path);
      }
    }
  };
  build(sectionsTree.value);
  return map;
});

// Compute the default location from the image's section
const defaultLocationPath = computed(() => {
  if (!props.image) return "";
  const sectionId =
    props.image.sortedSectionId ?? props.image.overviewSectionId;
  if (sectionId && sectionTitlePaths.value.has(sectionId)) {
    return sectionTitlePaths.value.get(sectionId)!;
  }
  return "";
});

const hasSortedSection = computed(() => {
  if (!props.image) return false;
  return !!(props.image.sortedSectionId || props.image.overviewSectionId);
});

// Local form state
const form = ref<{
  numbering: number | null;
  description: string;
  location: string;
}>({
  numbering: null,
  description: "",
  location: "",
});

// Sync form with image prop when dialog opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen && props.image) {
      form.value = {
        numbering: props.image.numbering ?? null,
        description: props.image.description ?? "",
        location: props.image.location ?? "",
      };
    }
  },
);

const show = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// Get the fileId - handle both ReportImage and MergedReportImage structures
const fileId = computed(() => {
  if (!props.image) return null;
  // MergedReportImage has fileId directly
  if ("fileId" in props.image && props.image.fileId) {
    return props.image.fileId;
  }
  // ReportImage has nested diagnosticImage
  if ("diagnosticImage" in props.image) {
    return props.image.diagnosticImage?.fileId ?? null;
  }
  return null;
});

// Check if this is a virtual/merged image that needs to be created first
const isVirtualImage = computed(() => {
  if (!props.image) return false;
  return "isVirtual" in props.image && props.image.isVirtual;
});

// Get report image ID (may be null for virtual images)
const reportImageId = computed(() => {
  if (!props.image) return null;
  // ReportImage has id directly
  if ("id" in props.image && !("isVirtual" in props.image)) {
    return props.image.id;
  }
  // MergedReportImage has reportImageId
  if ("reportImageId" in props.image) {
    return props.image.reportImageId;
  }
  return null;
});

// Get diagnostic image ID for creating new report images
const diagnosticImageId = computed(() => {
  if (!props.image) return null;
  if ("diagnosticImageId" in props.image) {
    return props.image.diagnosticImageId;
  }
  return null;
});

const handleSave = async () => {
  if (!props.image) return;

  const targetId = reportImageId.value;

  // Image virtuelle : créer le ReportImage (numéro rapport B + description/location).
  if (isVirtualImage.value && diagnosticImageId.value) {
    await createReportImage({
      diagnosticImageId: diagnosticImageId.value,
      reportId: props.reportId,
      numbering: form.value.numbering ?? undefined,
      description: form.value.description || undefined,
      location: form.value.location || undefined,
    });
    emit("saved");
    show.value = false;
    return;
  }

  if (!targetId) {
    emit("saved");
    show.value = false;
    return;
  }

  await updateImage({
    id: targetId,
    payload: {
      numbering: form.value.numbering,
      description: form.value.description,
      location: form.value.location,
    },
  });
  emit("saved");
  show.value = false;
};
</script>

<template>
  <el-dialog
    v-model="show"
    :title="t('report:photos.editDialog.title')"
    width="560px"
    append-to-body
  >
    <div v-if="image" class="d-flex flex-column gap-3">
      <!-- Image preview -->
      <div v-if="fileId" class="d-flex justify-content-center">
        <div class="image-preview rounded overflow-hidden">
          <VisualiseImage :key="fileId" :id="fileId" hide-preview />
        </div>
      </div>

      <!-- Identifiant photo : discret, sous la photo -->
      <div v-if="currentPhotoNumber != null" class="text-center">
        <el-tooltip
          :content="t('report:photos.editDialog.photoNumberTooltip')"
          placement="top"
          popper-class="identifier-tooltip"
        >
          <span class="small text-muted photo-identifier">
            {{ t("report:photos.editDialog.photoNumber") }} :
            <span class="fw-semibold">#{{ currentPhotoNumber }}</span>
            <i class="bi-info-circle ms-1"></i>
          </span>
        </el-tooltip>
      </div>

      <div class="flex-grow-1 d-flex flex-column gap-3">
        <!-- Champ principal : numéro imprimé dans le rapport -->
        <div>
          <label class="form-label small fw-medium">
            {{ t("report:photos.editDialog.reportNumber") }}
          </label>
          <el-input-number
            v-model="form.numbering"
            :min="1"
            controls-position="right"
            class="w-100"
          />
        </div>
        <div>
          <label class="form-label small fw-medium">
            {{ t("report:photos.column.description") }}
          </label>
          <el-input v-model="form.description" type="textarea" :rows="2" />
        </div>
        <div>
          <label class="form-label small fw-medium">
            {{ t("report:photos.column.location") }}
          </label>
          <el-input
            v-model="form.location"
            :placeholder="
              hasSortedSection
                ? defaultLocationPath
                : t('report:photos.editDialog.locationPlaceholder')
            "
          />
          <div
            v-if="hasSortedSection && !form.location.trim()"
            class="form-text text-muted"
          >
            {{ t("report:photos.editDialog.locationHint") }}
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-outline-secondary" @click="show = false">
          {{ t("cancel") }}
        </button>
        <button
          class="btn btn-primary"
          :disabled="isSaving"
          @click="handleSave"
        >
          <span
            v-if="isSaving"
            class="spinner-border spinner-border-sm me-2"
          ></span>
          {{ t("save") }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.image-preview {
  max-width: 100%;
  max-height: 280px;

  :deep(img) {
    max-width: 100%;
    max-height: 280px;
    width: auto !important;
    height: auto !important;
    object-fit: contain;
  }
}

.photo-identifier {
  cursor: help;
}
</style>

<!-- Non scoped : le popper du tooltip est téléporté dans <body> -->
<style lang="scss">
.identifier-tooltip {
  max-width: 280px;
}
</style>
